import { DIAGRAM_PITCH_HEIGHT } from './training.js';
/** So viele Strecken nutzt die Naeherung der Kurvenlaenge - fein genug fuer Dauer und Welle. */
export const CURVE_SEGMENTS = 32;
/** Groesster Rundungsradius einer Ecke des Linienzugs in Zeichnungseinheiten (kuerzere Strecken runden weniger). */
export const CORNER_RADIUS = 3;
/** So viele Stuecke hat die Rundung einer Ecke, wenn der Linienzug abgetastet wird. */
const CORNER_SAMPLES = 8;
/** So viele Stuecke hat jedes Bezierstueck einer glatten Kurve, wenn sie abgetastet wird (Laenge, Ablauf, Welle). */
export const SMOOTH_SAMPLES = 16;
/** Geometrie eines Pfeils aus seinen Feldern `from`, `to`, `via` und `points`. */
export function arrowGeometry(arrow) {
    const geometry = {
        from: { x: arrow.from[0], y: arrow.from[1] },
        to: { x: arrow.to[0], y: arrow.to[1] },
        via: arrow.via ? { x: arrow.via[0], y: arrow.via[1] } : null,
    };
    if (arrow.points && arrow.points.length > 0) {
        geometry.points = arrow.points.map((point) => ({ x: point[0], y: point[1] }));
        if (arrow.smooth)
            geometry.smooth = true;
    }
    return geometry;
}
/** Hat der Pfeil Knickpunkte (ist er ein Linienzug)? */
export function hasKnicks(geometry) {
    return (geometry.points?.length ?? 0) > 0;
}
/** Punkt des Pfeils bei t (0 = Anfang, 1 = Ende); ohne `via` auf der Geraden. Linienzuege: siehe `arrowFlatPath`. */
export function curvePoint(geometry, t) {
    const { from, to, via } = geometry;
    if (!via)
        return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
    const u = 1 - t;
    return {
        x: u * u * from.x + 2 * u * t * via.x + t * t * to.x,
        y: u * u * from.y + 2 * u * t * via.y + t * t * to.y,
    };
}
/**
 * Linienzug durch `vertices` mit gerundeten Ecken: Jede innere Ecke wird um `radius` (hoechstens die Haelfte der
 * anliegenden Strecken, damit sich Nachbarecken nie ueberschneiden) abgeschnitten und mit einer quadratischen
 * Kurve ueber die Ecke als Kontrollpunkt verbunden. Der Weg beginnt und endet genau an den Endpunkten, gerade.
 * Die Punkte muessen in gleich gewichteten Einheiten (Zeichnungseinheiten) stehen.
 */
export function roundedPolyline(vertices, radius = CORNER_RADIUS) {
    const start = vertices[0] ?? { x: 0, y: 0 };
    const ops = [];
    for (let index = 1; index < vertices.length - 1; index += 1) {
        const before = vertices[index - 1];
        const corner = vertices[index];
        const after = vertices[index + 1];
        const lengthIn = Math.hypot(before.x - corner.x, before.y - corner.y);
        const lengthOut = Math.hypot(after.x - corner.x, after.y - corner.y);
        const cut = Math.min(radius, lengthIn / 2, lengthOut / 2);
        if (cut < 1e-6) {
            ops.push({ kind: 'L', to: corner });
            continue;
        }
        ops.push({ kind: 'L', to: { x: corner.x + ((before.x - corner.x) / lengthIn) * cut, y: corner.y + ((before.y - corner.y) / lengthIn) * cut } }, { kind: 'Q', control: corner, to: { x: corner.x + ((after.x - corner.x) / lengthOut) * cut, y: corner.y + ((after.y - corner.y) / lengthOut) * cut } });
    }
    const last = vertices[vertices.length - 1];
    if (last && vertices.length > 1)
        ops.push({ kind: 'L', to: last });
    return { start, ops };
}
/** Der gerundete Linienzug als dichter Streckenzug (jede Rundung in `CORNER_SAMPLES` Stuecken). */
export function flattenPolyline(vertices, radius = CORNER_RADIUS) {
    const { start, ops } = roundedPolyline(vertices, radius);
    const points = [start];
    let last = start;
    for (const op of ops) {
        if (op.kind === 'L') {
            points.push(op.to);
        }
        else {
            for (let step = 1; step <= CORNER_SAMPLES; step += 1) {
                const t = step / CORNER_SAMPLES;
                const u = 1 - t;
                points.push({
                    x: u * u * last.x + 2 * u * t * op.control.x + t * t * op.to.x,
                    y: u * u * last.y + 2 * u * t * op.control.y + t * t * op.to.y,
                });
            }
        }
        last = op.to;
    }
    return points;
}
const length2 = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
/** Gewichtete Summe dreier Punkte geteilt durch `divisor`. */
function blend(a, wa, b, wb, c, wc, divisor) {
    return { x: (a.x * wa + b.x * wb + c.x * wc) / divisor, y: (a.y * wa + b.y * wb + c.y * wc) / divisor };
}
/**
 * Glatte Kurve durch `vertices`: zentripetaler Catmull-Rom-Spline (Alpha 0,5), in kubische Bezierstuecke umgerechnet - je
 * ein Stueck zwischen zwei Nachbarpunkten, die Kurve geht genau durch jeden Punkt und hat dort keinen Knick. Die zentripetale
 * Form schleift und uebersteigt nicht, wenn Punkte sehr ungleich weit auseinanderliegen. An den Enden spiegelt ein Hilfspunkt den
 * Nachbarn (der Weg laeuft dort ohne Schwung in die Richtung zum naechsten Punkt aus). Doppelte Punkte bleiben unschaedlich.
 * Die Punkte muessen in gleich gewichteten Einheiten (Zeichnungseinheiten) stehen.
 */
export function smoothPath(vertices) {
    const start = vertices[0] ?? { x: 0, y: 0 };
    const ops = [];
    if (vertices.length < 2)
        return { start, ops };
    if (vertices.length === 2) {
        const [a, b] = [vertices[0], vertices[1]];
        return { start, ops: [{ c1: { x: a.x + (b.x - a.x) / 3, y: a.y + (b.y - a.y) / 3 }, c2: { x: a.x + ((b.x - a.x) * 2) / 3, y: a.y + ((b.y - a.y) * 2) / 3 }, to: b }] };
    }
    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    const padded = [{ x: 2 * first.x - vertices[1].x, y: 2 * first.y - vertices[1].y }, ...vertices, { x: 2 * last.x - vertices[vertices.length - 2].x, y: 2 * last.y - vertices[vertices.length - 2].y }];
    for (let index = 1; index < padded.length - 2; index += 1) {
        const p0 = padded[index - 1];
        const p1 = padded[index];
        const p2 = padded[index + 1];
        const p3 = padded[index + 2];
        const d2 = Math.sqrt(length2(p1, p2));
        if (d2 < 1e-9) {
            ops.push({ c1: p1, c2: p2, to: p2 });
            continue;
        }
        // Gleich weit entfernte Nachbarn (doppelter Punkt): der Abstand zum Mittelstueck vertritt den fehlenden.
        const gap1 = length2(p0, p1);
        const gap3 = length2(p2, p3);
        const d1 = gap1 < 1e-6 ? d2 : Math.sqrt(gap1);
        const d3 = gap3 < 1e-6 ? d2 : Math.sqrt(gap3);
        const c1 = blend(p2, d1 * d1, p0, -(d2 * d2), p1, 2 * d1 * d1 + 3 * d1 * d2 + d2 * d2, 3 * d1 * (d1 + d2));
        const c2 = blend(p1, d3 * d3, p3, -(d2 * d2), p2, 2 * d3 * d3 + 3 * d3 * d2 + d2 * d2, 3 * d3 * (d3 + d2));
        ops.push({ c1, c2, to: p2 });
    }
    return { start, ops };
}
/** Die glatte Kurve als dichter Streckenzug: jedes Bezierstueck in `samples` Strecken; die Punkte selbst sind Ecken des Streckenzugs. */
export function flattenSmooth(vertices, samples = SMOOTH_SAMPLES) {
    const { start, ops } = smoothPath(vertices);
    const points = [start];
    let last = start;
    for (const op of ops) {
        for (let step = 1; step <= samples; step += 1) {
            const t = step / samples;
            const u = 1 - t;
            const w0 = u * u * u;
            const w1 = 3 * u * u * t;
            const w2 = 3 * u * t * t;
            const w3 = t * t * t;
            points.push(step === samples ? op.to : { x: w0 * last.x + w1 * op.c1.x + w2 * op.c2.x + w3 * op.to.x, y: w0 * last.y + w1 * op.c1.y + w2 * op.c2.y + w3 * op.to.y });
        }
        last = op.to;
    }
    return points;
}
export function makeFlatPath(points) {
    const offsets = [];
    let length = 0;
    points.forEach((point, index) => {
        const before = points[index - 1];
        if (before)
            length += Math.hypot(point.x - before.x, point.y - before.y);
        offsets.push(length);
    });
    return { points: [...points], offsets, length };
}
/**
 * Ort und Laufrichtung (Einheitsvektor) auf dem Weg nach `along` Zeichnungseinheiten Weg; ausserhalb bleibt
 * der Ort am Anfang oder Ende. Die Richtung ist die der Strecke, auf der der Ort liegt.
 */
export function flatPathAt(path, along) {
    const { points, offsets } = path;
    const last = points.length - 1;
    if (last < 1)
        return { x: points[0]?.x ?? 0, y: points[0]?.y ?? 0, ux: 0, uy: 0 };
    const s = Math.min(path.length, Math.max(0, along));
    let index = 1;
    while (index < last && offsets[index] < s)
        index += 1;
    // Streckenstuecke ohne Laenge (doppelte Punkte) ueberspringen: dahinter folgt die richtige Richtung.
    while (index < last && offsets[index] - offsets[index - 1] < 1e-9)
        index += 1;
    const a = points[index - 1];
    const b = points[index];
    const span = offsets[index] - offsets[index - 1];
    if (span < 1e-9)
        return { x: b.x, y: b.y, ux: 0, uy: 0 };
    const f = Math.min(1, Math.max(0, (s - offsets[index - 1]) / span));
    return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, ux: (b.x - a.x) / span, uy: (b.y - a.y) / span };
}
/** Eckpunkte des Linienzugs eines Pfeils (Anfang, Knickpunkte, Ende) in Zeichnungseinheiten. */
function drawingVertices(pitch, geometry) {
    const scale = DIAGRAM_PITCH_HEIGHT[pitch] / 100;
    return [geometry.from, ...(geometry.points ?? []), geometry.to].map((point) => ({ x: point.x, y: point.y * scale }));
}
/**
 * Weg eines Pfeils mit Knickpunkten in Zeichnungseinheiten (gerundete Ecken oder, mit `smooth`, glatte Kurve durch alle
 * Punkte; abgetastet); `null` bei Pfeilen ohne Knickpunkte.
 */
export function arrowFlatPath(pitch, geometry) {
    if (!hasKnicks(geometry))
        return null;
    const vertices = drawingVertices(pitch, geometry);
    return makeFlatPath(geometry.smooth ? flattenSmooth(vertices) : flattenPolyline(vertices));
}
/** Punkt eines Weges in Zeichnungseinheiten als Feldpunkt (Prozent); `fraction` ist der Anteil 0 bis 1 der Weglaenge. */
export function flatPathField(pitch, path, fraction) {
    const at = flatPathAt(path, fraction * path.length);
    return { x: at.x, y: (at.y * 100) / DIAGRAM_PITCH_HEIGHT[pitch] };
}
/**
 * Laufrichtung (Tangente) bei t als Einheitsvektor in Zeichnungseinheiten, die y-Achse
 * mit der Feldhoehe gestreckt. Bei verschwindender Ableitung (Kontrollpunkt liegt
 * auf dem Anfang oder Ende) gilt die Richtung von Anfang zu Ende. Bei Linienzuegen ist t der
 * Anteil an der Weglaenge.
 */
export function curveTangent(pitch, geometry, t) {
    const flat = arrowFlatPath(pitch, geometry);
    if (flat) {
        const at = flatPathAt(flat, t * flat.length);
        return { x: at.ux, y: at.uy };
    }
    const { from, to, via } = geometry;
    const scale = DIAGRAM_PITCH_HEIGHT[pitch] / 100;
    let dx;
    let dy;
    if (via) {
        dx = 2 * (1 - t) * (via.x - from.x) + 2 * t * (to.x - via.x);
        dy = (2 * (1 - t) * (via.y - from.y) + 2 * t * (to.y - via.y)) * scale;
    }
    else {
        dx = to.x - from.x;
        dy = (to.y - from.y) * scale;
    }
    let length = Math.hypot(dx, dy);
    if (length < 1e-9) {
        dx = to.x - from.x;
        dy = (to.y - from.y) * scale;
        length = Math.hypot(dx, dy);
    }
    return length < 1e-9 ? { x: 0, y: 0 } : { x: dx / length, y: dy / length };
}
/** Angenaeherte Laenge des Pfeils in Zeichnungseinheiten (Streckenzug aus `CURVE_SEGMENTS` Stuecken; Linienzug mit gerundeten Ecken). */
export function curveLength(pitch, geometry) {
    const flat = arrowFlatPath(pitch, geometry);
    if (flat)
        return flat.length;
    const scale = DIAGRAM_PITCH_HEIGHT[pitch] / 100;
    const { from, to, via } = geometry;
    if (!via)
        return Math.hypot(to.x - from.x, (to.y - from.y) * scale);
    let length = 0;
    let last = from;
    for (let step = 1; step <= CURVE_SEGMENTS; step += 1) {
        const next = curvePoint(geometry, step / CURVE_SEGMENTS);
        length += Math.hypot(next.x - last.x, (next.y - last.y) * scale);
        last = next;
    }
    return length;
}
/** Kontrollpunkt, damit die Kurve von `from` nach `to` genau durch `middle` laeuft (bei t = 0,5). */
export function viaForMiddle(from, to, middle) {
    return { x: 2 * middle.x - (from.x + to.x) / 2, y: 2 * middle.y - (from.y + to.y) / 2 };
}
//# sourceMappingURL=training-curve.js.map